import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "'I' statements in communication serve primarily to:",
    options: [
      "Blame the other person for your feelings",
      "Express your feelings and needs without blaming",
      "Make the other person feel guilty",
      "Avoid taking responsibility for your emotions"
    ],
    correctAnswer: 1,
    explanation: "'I' statements allow you to take ownership of your feelings while describing a situation, which leads to better understanding and less defensiveness."
  },
  {
    question: "Active listening includes all of the following EXCEPT:",
    options: [
      "Maintaining eye contact and body language that shows interest",
      "Reflecting back what you heard to confirm understanding",
      "Thinking about your response while the other person is still talking",
      "Asking clarifying questions without judgment"
    ],
    correctAnswer: 2,
    explanation: "Interrupting mentally or preparing your response prevents true listening. Active listening requires full presence and attention to the speaker."
  },
  {
    question: "Healthy boundaries in a relationship involve:",
    options: [
      "Telling others exactly what they can and cannot do",
      "Never saying no to maintain peace",
      "Clearly communicating your limits while respecting others' autonomy",
      "Withdrawing from all relationships to avoid conflict"
    ],
    correctAnswer: 2,
    explanation: "Healthy boundaries involve clear communication of your needs and limits, while acknowledging that others have their own needs and right to choose."
  },
  {
    question: "When someone violates a boundary you've set, the most assertive response is to:",
    options: [
      "Ignore it and hope it doesn't happen again",
      "Respond with anger and blame",
      "Calmly restate your boundary and follow through with consequences",
      "Change your boundary to avoid conflict"
    ],
    correctAnswer: 2,
    explanation: "Consistency and calm clarity reinforce your boundaries. Following through shows your boundaries are serious, which helps others respect them."
  },
  {
    question: "Assertiveness differs from aggression primarily in that it:",
    options: [
      "Involves more direct language",
      "Respects both your needs and others' rights",
      "Forces others to comply with your wishes",
      "Prioritizes winning the argument"
    ],
    correctAnswer: 1,
    explanation: "Assertiveness honors mutual respect, while aggression disregards others' needs. Assertive communication is direct but considerate."
  }
];

const EmotioPsychoRationalModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('emotioPsychoRationalM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/emotio-psycho-rational-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Effective communication skills including 'I' statements
              </li>
              <li>
                How to practice active listening
              </li>
              <li>
                Setting and maintaining healthy boundaries
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Practice these communication skills in your daily interactions!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Emotio-Psycho-Rational Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left emotio-psycho-rational-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Stress Management and Resilience</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about stress management and resilience.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>
            
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule4QuizPage;


